import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { User } from "../../types";
import { useGetTrialsUserManagementQuery } from "../../dataAccess";
import theme from "../../../../app/theme";

interface AssignTrialsModalProps {
    open: boolean;
    user: User | null;
    onClose: () => void;
    onSave: (userId: string, newTrialIds: string[]) => void;
}

const AssignTrialsModal: React.FC<AssignTrialsModalProps> = ({ open, user, onClose, onSave }) => {
    const [selectedTrial, setSelectedTrial] = useState<string>("");
    const [assignments, setAssignments] = useState<{ name: string; uuid: string }[]>([]);
    const { data: availableTrials } = useGetTrialsUserManagementQuery();

    useEffect(() => {
        if (open && user) {
            const currentTrials = user.trials || [];
            setAssignments(currentTrials.map((t) => ({ name: t.trialName, uuid: t.uuid })));
            setSelectedTrial("");
        }
    }, [open, user]);

    if (!user) return null;

    const handleAddTrial = () => {
        if (!selectedTrial || !availableTrials) return;

        const trial = availableTrials.find((t) => t.uuid === selectedTrial);
        if (!trial) return;

        const isAlreadyInAssignments = assignments.some((t) => t.uuid === trial.uuid);

        if (!isAlreadyInAssignments) {
            setAssignments([...assignments, trial]);
        }
        setSelectedTrial("");
    };

    const handleRemoveAssignment = (uuid: string) => {
        setAssignments(assignments.filter((t) => t.uuid !== uuid));
    };

    const handleSave = () => {
        const trialIds = assignments.map((t) => t.uuid);
        onSave(user.uuid, trialIds);
        onClose();
    };

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        setSelectedTrial(event.target.value);
    };

    const availableTrialsFiltered = availableTrials?.filter((trial) => !assignments.some((a) => a.uuid === trial.uuid));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">Assign Trials to User</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Add new trial assignments for {user.email}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pt: 20, pb: 3 }}>
                <Box mb={20}>
                    <Typography variant="subtitle2" sx={{ mb: 8, color: "#fff" }}>
                        Trial
                    </Typography>
                    <Box display="flex" gap={8}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: "#aaa" }}>
                                {availableTrialsFiltered && availableTrialsFiltered.length === 0
                                    ? "All trials assigned"
                                    : "Search trials..."}
                            </InputLabel>
                            <Select
                                value={selectedTrial}
                                onChange={handleSelectChange}
                                label={
                                    availableTrialsFiltered && availableTrialsFiltered.length === 0
                                        ? "All trials assigned"
                                        : "Search trials..."
                                }
                                disabled={!availableTrialsFiltered || availableTrialsFiltered.length === 0}
                                sx={{
                                    "bgcolor": theme.palette.background.paper,
                                    "color": "#fff",
                                    ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                                    ".MuiSvgIcon-root": { color: "#aaa" },
                                }}
                            >
                                {availableTrialsFiltered?.map((trial) => (
                                    <MenuItem key={trial.uuid} value={trial.uuid}>
                                        {trial.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            sx={{
                                minWidth: "80px",
                            }}
                            onClick={handleAddTrial}
                            disabled={
                                !selectedTrial || !availableTrialsFiltered || availableTrialsFiltered.length === 0
                            }
                        >
                            Add
                        </Button>
                    </Box>
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 8, color: "#aaa" }}>
                        New Assignments
                    </Typography>
                    <Box sx={{ bgcolor: theme.palette.background.paper, borderRadius: 1, p: 10, minHeight: "60px" }}>
                        {assignments.length > 0 ? (
                            <Box display="flex" flexWrap="wrap" gap={8}>
                                {assignments.map((trial) => (
                                    <Chip
                                        key={trial.uuid}
                                        label={trial.name}
                                        onDelete={() => handleRemoveAssignment(trial.uuid)}
                                        sx={{
                                            "bgcolor": "rgba(76, 175, 80, 0.15)",
                                            "color": "#4caf50",
                                            "border": "1px solid #4caf50",
                                            ".MuiChip-deleteIcon": { color: "#4caf50" },
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                No trials assigned
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.background.paper, py: 16, px: 24 }}>
                <Button onClick={onClose} sx={{ mr: 2 }}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave}>
                    Assign ({assignments.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignTrialsModal;
